import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AiChatScope } from "./AiChat.types";
import { RailWidthState, useRailWidthState } from "../main/rail/Rail.hooks";
import { getSidePanelWidth, minSidePanelWidth } from "@common/sidePanelWidth";

const SCOPES_STORAGE_KEY = "ai-chat-scopes-v1";

// Split in two on purpose. A single context value rebuilt every render meant that any change in here —
// opening the chat, staging a prompt, committing a drag — re-rendered every consumer, and the consumers
// included the top bar and every page that can open the entity panel (a traced drag release cost 560 ms
// of page-wide React render). Pages and the top bar only ever call actions, so they subscribe to an object
// whose identity never changes; only the chat itself and the rail lay out with the state.
interface AiChatState {
    isOpen: boolean;
    isExpanded: boolean;

    // The one width the rail has, whichever occupant is showing. Held here rather than per occupant
    // because a user who drags the chat wider expects the entity panel to open at that width too, and
    // the entity panel provider sits inside this one.
    widthState: RailWidthState;

    // These read scopes/transientScope from render scope, which is why they sit with the state rather
    // than the actions. Their only callers are inside the chat.
    getScope: (conversationId: string | null) => AiChatScope | undefined;
    bindTransientScopeToConversation: (newConversationId: string) => void;

    // One-shot slot for staging a prompt into the input (e.g. clicking a suggestion chip).
    // AiChatInput watches this and copies it into its local state, then calls setPendingPrompt(undefined)
    // to consume it. Kept as a single slot (not per-conversation) because staging is transient by
    // nature — once consumed, the value lives in the input's local state.
    pendingPrompt: string | undefined;
}

interface AiChatActions {
    setIsOpen: (open: boolean) => void;
    toggleOpen: () => void;
    setIsExpanded: (expanded: boolean) => void;
    setScope: (conversationId: string | null, scope: AiChatScope | undefined) => void;
    removeScope: (conversationId: string) => void;
    setPendingPrompt: (value: string | undefined) => void;
}

const AiChatStateContext = createContext<AiChatState>({
    isOpen: false,
    isExpanded: false,
    widthState: { width: getSidePanelWidth(1280), setWidth: () => {}, minWidth: minSidePanelWidth },
    getScope: () => undefined,
    bindTransientScopeToConversation: () => {},
    pendingPrompt: undefined,
});

const AiChatActionsContext = createContext<AiChatActions>({
    setIsOpen: () => {},
    toggleOpen: () => {},
    setIsExpanded: () => {},
    setScope: () => {},
    removeScope: () => {},
    setPendingPrompt: () => {},
});

function loadScopesFromStorage(): Record<string, AiChatScope> {
    try {
        const raw = localStorage.getItem(SCOPES_STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Record<string, AiChatScope>) : {};
    } catch {
        return {};
    }
}

export function AiChatProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [scopes, setScopes] = useState<Record<string, AiChatScope>>(loadScopesFromStorage);
    // Transient scope held while the user is composing a message in a brand-new chat
    // before its server-assigned conversationId arrives. Bound to the id once the
    // "done" event fires for the new conversation. Not persisted — losing it on
    // refresh is acceptable since no message was saved server-side either.
    const [transientScope, setTransientScope] = useState<AiChatScope | undefined>(undefined);
    const [pendingPrompt, setPendingPrompt] = useState<string | undefined>(undefined);
    const widthState = useRailWidthState("rail-width", getSidePanelWidth, minSidePanelWidth);

    useEffect(() => {
        if (!isOpen) setIsExpanded(false);
    }, [isOpen]);

    useEffect(() => {
        try {
            localStorage.setItem(SCOPES_STORAGE_KEY, JSON.stringify(scopes));
        } catch {
            // Storage quota errors are non-fatal — scope just won't persist this session.
        }
    }, [scopes]);

    function getScope(conversationId: string | null) {
        return conversationId === null ? transientScope : scopes[conversationId];
    }

    function bindTransientScopeToConversation(newConversationId: string) {
        if (!transientScope) return;
        setScopes((prev) => ({ ...prev, [newConversationId]: transientScope }));
        setTransientScope(undefined);
    }

    // useMemo strictly for performance (see the note on the split above): every member reaches state
    // through a setState function or a functional update, never through render scope, so the object can
    // be built once and consumers of the actions context never re-render because of the chat.
    const actions = useMemo<AiChatActions>(
        () => ({
            setIsOpen,
            toggleOpen: () => setIsOpen((prev) => !prev),
            setIsExpanded,
            setScope: (conversationId, scope) => {
                if (conversationId === null) {
                    setTransientScope(scope);
                    return;
                }
                setScopes((prev) => {
                    const next = { ...prev };
                    if (scope === undefined) {
                        delete next[conversationId];
                    } else {
                        next[conversationId] = scope;
                    }
                    return next;
                });
            },
            removeScope: (conversationId) => {
                setScopes((prev) => {
                    if (!(conversationId in prev)) return prev;
                    const next = { ...prev };
                    delete next[conversationId];
                    return next;
                });
            },
            setPendingPrompt,
        }),
        []
    );

    return (
        <AiChatActionsContext.Provider value={actions}>
            <AiChatStateContext.Provider
                value={{
                    isOpen,
                    isExpanded,
                    widthState,
                    getScope,
                    bindTransientScopeToConversation,
                    pendingPrompt,
                }}
            >
                {children}
            </AiChatStateContext.Provider>
        </AiChatActionsContext.Provider>
    );
}

export function useAiChatState() {
    return useContext(AiChatStateContext);
}

export function useAiChatActions() {
    return useContext(AiChatActionsContext);
}
